import { axiosClient } from '../../../library/Service/axiosClient';
import PageConfigInquiry from './PageConfigInquiry.json';

class InquiryReportService {
    static pageLoader(params: any): Promise<any> {
        const state: any = {};
        const searchCriteria = this.getDefaultSearchCriteria();

        return Promise.all([
            this.getPageConfig(),
            this.search(searchCriteria),
            this.getFilters(),
            this.getStats(searchCriteria),
        ]).then(([formConfig, inquiryResponse, filterDomains, stats]) => {
            const payload = inquiryResponse?.data ?? {};

            state.formConfig = formConfig;
            state.data = {
                inquiryList: payload?.data ?? [],
                pagination: {
                    total: payload?.total ?? 0,
                    page: searchCriteria.page,
                    limit: searchCriteria.limit,
                },
                searchCriteria,
                filterDomains,
                stats,
            };
            state.routeInfo = params;
            state.internal = { gridState: {} };

            return Promise.resolve(state);
        }).catch((error) => {
            console.error('Error loading Inquiry Management:', error);
            return Promise.resolve({
                formConfig: PageConfigInquiry,
                data: {
                    inquiryList: [],
                    pagination: { total: 0, page: 1, limit: 20 },
                    searchCriteria,
                    filterDomains: { status: [], outcome: [] },
                    stats: { total: 0, new: 0, responded: 0, converted: 0 },
                },
                routeInfo: params,
                internal: { gridState: {} },
            });
        });
    }

    static getPageConfig() {
        return Promise.resolve(PageConfigInquiry);
    }

    static getDefaultSearchCriteria() {
        return {
            search: '',
            status: '',
            outcome: '',
            fromDate: '',
            toDate: '',
            dateRange: '',
            sort: 'newest',
            page: 1,
            limit: 20,
        };
    }

    static search(searchCriteria: any) {
        return axiosClient().post(
            `${process.env.REACT_APP_REPORTS_SERVER}/business-directory/search-inquiry-report`,
            this.sanitizeCriteria(searchCriteria)
        );
    }

    static async getFilters() {
        try {
            const response = await axiosClient().get(
                `${process.env.REACT_APP_REPORTS_SERVER}/business-directory/inquiry-report-filters`
            );

            return {
                status: response?.data?.status ?? [],
                outcome: response?.data?.outcome ?? [],
            };
        } catch (error) {
            console.error('Failed to fetch inquiry filters:', error);
            return { status: [], outcome: [] };
        }
    }

    static async getStats(searchCriteria: any) {
        try {
            const statsCriteria = this.sanitizeCriteria({
                ...searchCriteria,
                status: '',
                outcome: '',
                page: 1,
                limit: 1,
            });

            const response = await axiosClient().post(
                `${process.env.REACT_APP_REPORTS_SERVER}/business-directory/inquiry-report-stats`,
                statsCriteria
            );

            return {
                total: response?.data?.total ?? 0,
                new: response?.data?.new ?? 0,
                responded: response?.data?.responded ?? 0,
                converted: response?.data?.converted ?? 0,
            };
        } catch (error) {
            console.error('Failed to fetch inquiry stats:', error);
            return { total: 0, new: 0, responded: 0, converted: 0 };
        }
    }

    static exportCSV(searchCriteria: any) {
        const criteria = this.sanitizeCriteria(searchCriteria);
        const { page, limit, dateRange, ...exportCriteria } = criteria;

        return axiosClient().get(
            `${process.env.REACT_APP_REPORTS_SERVER}/business-directory/export-inquiry-report`,
            {
                params: exportCriteria,
                responseType: 'blob',
            }
        ).then((response) => {
            const url = window.URL.createObjectURL(new Blob([response.data]));
            const link = document.createElement('a');
            link.href = url;
            link.setAttribute('download', `inquiries_${Date.now()}.csv`);
            document.body.appendChild(link);
            link.click();
            link.parentNode?.removeChild(link);
            window.URL.revokeObjectURL(url);
        });
    }

    static updateInquiry(inquiryId: string | number, payload: any) {
        return axiosClient().patch(
            `${process.env.REACT_APP_COMMON_API}/provider-inquiries/${inquiryId}`,
            payload
        );
    }

    static getMessages(inquiryId: string | number) {
        return axiosClient().get(`${process.env.REACT_APP_COMMON_API}/provider-inquiries/${inquiryId}/messages`);
    }

    static sendMessage(inquiryId: string | number, payload: { message: string }) {
        return axiosClient().post(`${process.env.REACT_APP_COMMON_API}/provider-inquiries/${inquiryId}/message`, payload);
    }

    static sanitizeCriteria(searchCriteria: any) {
        const criteria: any = {};

        if (searchCriteria.search !== undefined && searchCriteria.search !== null) {
            criteria.search = String(searchCriteria.search).trim();
        }

        if (searchCriteria.status) {
            criteria.status = searchCriteria.status;
        }

        if (searchCriteria.outcome) {
            criteria.outcome = searchCriteria.outcome;
        }

        if (searchCriteria.fromDate) {
            criteria.fromDate = searchCriteria.fromDate;
        }

        if (searchCriteria.toDate) {
            criteria.toDate = searchCriteria.toDate;
        }

        if (searchCriteria.sort) {
            criteria.sort = searchCriteria.sort;
        }

        if (searchCriteria.page) {
            criteria.page = searchCriteria.page;
        }

        if (searchCriteria.limit) {
            criteria.limit = searchCriteria.limit;
        }

        return criteria;
    }
}

export default InquiryReportService;